import { memo, useEffect, useMemo, useState } from "react";

import {
  resolveUserAvatarGenderVariant,
  resolveUserAvatarUrl,
} from "../../utils/userAvatar";
import styles from "./UserAvatar.module.css";

function buildAvatarLabel(name, alt) {
  if (typeof alt === "string") {
    return alt;
  }

  return name ? `Avatar de ${name}` : "Avatar utilisateur";
}

function FallbackAvatarGlyph({ variant }) {
  if (variant === "feminine") {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true" className={styles.fallbackGlyph}>
        <path
          className={styles.fallbackGlyphAccent}
          d="M24 8.8c-6.3 0-11.5 5.1-11.5 11.4v3.4h3.1v-3.2c0-4.7 3.8-8.5 8.4-8.5s8.4 3.8 8.4 8.5v3.2h3.1v-3.4C35.5 13.9 30.3 8.8 24 8.8z"
        />
        <circle className={styles.fallbackGlyphFace} cx="24" cy="19.1" r="5.9" />
        <path
          className={styles.fallbackGlyphBody}
          d="M10.6 39.5c1.6-6.2 6.6-10 13.4-10s11.8 3.8 13.4 10l-2.9.8c-1.3-5-5.2-8-10.5-8s-9.2 3-10.5 8z"
        />
      </svg>
    );
  }

  if (variant === "masculine") {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true" className={styles.fallbackGlyph}>
        <path
          className={styles.fallbackGlyphAccent}
          d="M15.6 15.1c1.2-3.3 4.5-5.6 8.4-5.6s7.2 2.3 8.4 5.6l-2.7.9c-.8-2.1-3-3.6-5.7-3.6s-4.9 1.4-5.7 3.6z"
        />
        <circle className={styles.fallbackGlyphFace} cx="24" cy="19.2" r="5.8" />
        <path
          className={styles.fallbackGlyphBody}
          d="M9.9 39.8c1.4-6.6 6.3-10.6 14.1-10.6s12.7 4 14.1 10.6l-3 .6c-1.1-5.2-4.8-8.2-11.1-8.2s-10 3-11.1 8.2z"
        />
      </svg>
    );
  }

  if (variant === "couple") {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true" className={styles.fallbackGlyph}>
        {/* silhouette femme (gauche) */}
        <circle className={styles.fallbackGlyphFace}   cx="17" cy="17" r="5" />
        <path   className={styles.fallbackGlyphBody}   d="M6 38c1.1-5 5-8 11-8s9.9 3 11 8" />
        {/* silhouette homme (droite, décalé) */}
        <circle className={styles.fallbackGlyphAccent} cx="31" cy="17" r="5" />
        <path   className={styles.fallbackGlyphBody}   d="M20 38c1.1-5 5-8 11-8s9.9 3 11 8" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={styles.fallbackGlyph}>
      <circle className={styles.fallbackGlyphAccent} cx="24" cy="11.9" r="2.1" />
      <circle className={styles.fallbackGlyphFace} cx="24" cy="19.2" r="5.9" />
      <path
        className={styles.fallbackGlyphBody}
        d="M10.4 39.6c1.5-6.3 6.6-10.2 13.6-10.2s12.1 3.9 13.6 10.2l-2.9.7c-1.2-5.1-5.1-8.1-10.7-8.1s-9.5 3-10.7 8.1z"
      />
    </svg>
  );
}

function UserAvatar({
  user,
  src = "",
  name = "",
  alt,
  size = 48,
  className = "",
  ringClassName = "",
  imageClassName = "",
  fallbackClassName = "",
  loading = "lazy",
  decoding = "async",
  fetchpriority = "low",
  title,
}) {
  const [imageMissing, setImageMissing] = useState(false);
  const resolvedName =
    name ||
    user?.displayName ||
    user?.username ||
    "";
  const resolvedAlt = buildAvatarLabel(resolvedName, alt);
  const resolvedSrc = useMemo(
    () => resolveUserAvatarUrl(user, src),
    [src, user]
  );
  const fallbackVariant = useMemo(
    () => resolveUserAvatarGenderVariant(user),
    [user]
  );
  const fallbackVariantClassName =
    fallbackVariant === "feminine"  ? styles.fallbackFeminine  :
    fallbackVariant === "masculine" ? styles.fallbackMasculine :
    fallbackVariant === "couple"    ? styles.fallbackCouple    :
    styles.fallbackNeutral;

  const ringVariantClassName =
    fallbackVariant === "feminine"  ? styles.ringFeminine  :
    fallbackVariant === "masculine" ? styles.ringMasculine :
    fallbackVariant === "couple"    ? styles.ringCouple    :
    "";
  const avatarStyle = useMemo(
    () => ({
      "--user-avatar-size": `${Math.max(24, Number(size) || 48)}px`,
    }),
    [size]
  );

  useEffect(() => {
    setImageMissing(false);
  }, [resolvedSrc]);

  const showImage = Boolean(resolvedSrc) && !imageMissing;

  return (
    <span
      className={`${styles.root} ${className}`.trim()}
      style={avatarStyle}
      title={title}
      role={!showImage && resolvedAlt ? "img" : undefined}
      aria-label={!showImage && resolvedAlt ? resolvedAlt : undefined}
    >
      <span className={`${styles.ring} ${ringVariantClassName} ${ringClassName}`.trim()}>
        {showImage ? (
          <img
            src={resolvedSrc}
            alt={resolvedAlt}
            className={`${styles.image} ${imageClassName}`.trim()}
            loading={loading}
            decoding={decoding}
            fetchpriority={fetchpriority}
            onError={() => setImageMissing(true)}
          />
        ) : (
          <span
            className={`${styles.fallback} ${fallbackVariantClassName} ${fallbackClassName}`.trim()}
            aria-hidden="true"
          >
            <span className={styles.fallbackSurface} />
            <FallbackAvatarGlyph variant={fallbackVariant} />
          </span>
        )}
      </span>
    </span>
  );
}

export default memo(UserAvatar);
